import { useQueries, useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  type AggregatedIssue,
  aggregateIssuesQueryOptions,
  issueListQueryOptions,
  useFavoriteIssueMutation,
  useHideIssueMutation,
} from '@/entities/issue';
import { signalListQueryOptions } from '@/entities/signal';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import { toast } from '@/stores/pure/ToastStore';
import { getTimezoneDayjs } from '@/utils/time';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import {
  convertDateValueToSignalDatePreset,
  DEFAULT_SIGNAL_DATE_SELECTION,
  parseSignalDatePreset,
  parseSignalPeriod,
  parseSignalPeriodValue,
  toSignalDateSelection,
} from '../lib/signal-date-preset.utils';
import type {
  SIGNAL_PERIOD,
  SignalDateSelection,
  SignalDateSelectionInput,
} from '../model';
import { signalDateSelectionSchema } from '../model';

export const useIssueListQuery = (props: {
  filterParams: StructuredFilter;
  period: SIGNAL_PERIOD;
  issue?: string;
}) => {
  const { filterParams, period, issue } = props;

  const wa = getCurrWorkspaceAlias();

  const { data: originIssues = [], isLoading: originIssueFetching } = useQuery(
    issueListQueryOptions({ workspaceId: wa }),
  );

  const aggregateQueries = useQueries({
    queries: splitAggregateFilterByMaxDays(filterParams).map((filter) =>
      aggregateIssuesQueryOptions({
        filter,
        period,
        issue,
        workspaceId: wa,
      }),
    ),
  });
  const issueAggregates = resolveAggregatedIssueQueries(aggregateQueries);
  const aggregateFetching = aggregateQueries.some((query) => query.isLoading);

  const { data: signals = [], isLoading: signalsFetching } = useQuery(
    signalListQueryOptions({ workspaceId: wa }),
  );

  return {
    originIssues,
    issueAggregates,
    signals,
    aggregateFetching,
    originIssueFetching,
    signalsFetching,
  };
};

const MAX_AGGREGATE_QUERY_DAYS = 90;

export const splitAggregateFilterByMaxDays = (
  filter: StructuredFilter,
): StructuredFilter[] => {
  if (typeof filter.start_date !== 'string' || typeof filter.end_date !== 'string') {
    return [filter];
  }

  const start = getTimezoneDayjs(filter.start_date).startOf('day');
  const end = getTimezoneDayjs(filter.end_date).endOf('day');
  const inclusiveDays = end.startOf('day').diff(start, 'day') + 1;
  if (inclusiveDays <= MAX_AGGREGATE_QUERY_DAYS) return [filter];

  const firstEnd = start
    .addVal(MAX_AGGREGATE_QUERY_DAYS - 1, 'day')
    .endOf('day');
  return [
    {
      ...filter,
      start_date: start.toJSON(),
      end_date: firstEnd.toJSON(),
    },
    {
      ...filter,
      start_date: firstEnd.addVal(1, 'millisecond').toJSON(),
      end_date: end.toJSON(),
    },
  ];
};

export const mergeAggregatedIssues = (
  issues: AggregatedIssue[],
): AggregatedIssue[] => {
  const mergedByName = new Map<string, AggregatedIssue>();
  for (const issue of issues) {
    const current = mergedByName.get(issue.name);
    mergedByName.set(
      issue.name,
      current
        ? { ...current, values: [...current.values, ...issue.values] }
        : { ...issue, values: [...issue.values] },
    );
  }
  return [...mergedByName.values()];
};

export const resolveAggregatedIssueQueries = (
  queries: Array<{ data?: AggregatedIssue[]; error?: unknown }>,
) => {
  const failedQuery = queries.find((query) => query.error != null);
  if (failedQuery?.error != null) throw failedQuery.error;
  return mergeAggregatedIssues(queries.flatMap((query) => query.data ?? []));
};

export const DYNAMIC_FILTER_QUERY_KEY = 'dynamic_filter_set';
export const PERIOD_QUERY_KEY = 'period';
export const PERIOD_VALUE_QUERY_KEY = 'period_value';
export const DATE_TYPE_QUERY_KEY = 'date_type';
export const START_DATE_QUERY_KEY = 'start_date';
export const END_DATE_QUERY_KEY = 'end_date';

export const buildIssueDetailSearchParams = (props: {
  dynamicFilterSet: StructuredFilter;
  signalDatePreset: SignalDateSelectionInput;
}) => {
  const selection = toSignalDateSelection(props.signalDatePreset);
  const params = new URLSearchParams({
    [DYNAMIC_FILTER_QUERY_KEY]: JSON.stringify(props.dynamicFilterSet),
  });
  if (selection.type === 'custom') {
    params.set(DATE_TYPE_QUERY_KEY, 'custom');
    params.set(START_DATE_QUERY_KEY, selection.startDate);
    params.set(END_DATE_QUERY_KEY, selection.endDate);
    return params;
  }

  const { unit, value } = parseSignalDatePreset(selection);
  params.set(DATE_TYPE_QUERY_KEY, 'preset');
  params.set(PERIOD_QUERY_KEY, unit);
  params.set(PERIOD_VALUE_QUERY_KEY, value.toString());
  return params;
};

export const parseIssueDetailDateSelection = (
  searchParams: URLSearchParams,
): SignalDateSelection => {
  const startDate = searchParams.get(START_DATE_QUERY_KEY);
  const endDate = searchParams.get(END_DATE_QUERY_KEY);
  if (
    searchParams.get(DATE_TYPE_QUERY_KEY) === 'custom' &&
    startDate &&
    endDate
  ) {
    const parsed = signalDateSelectionSchema.safeParse({
      type: 'custom',
      startDate,
      endDate,
    });
    return parsed.success ? parsed.data : DEFAULT_SIGNAL_DATE_SELECTION;
  }

  const period = searchParams.get(PERIOD_QUERY_KEY);
  const periodValue = searchParams.get(PERIOD_VALUE_QUERY_KEY);
  if (period && periodValue) {
    try {
      return {
        type: 'preset',
        preset: convertDateValueToSignalDatePreset(
          parseSignalPeriod(period),
          parseSignalPeriodValue(periodValue),
        ),
      };
    } catch {
      return DEFAULT_SIGNAL_DATE_SELECTION;
    }
  }

  return DEFAULT_SIGNAL_DATE_SELECTION;
};

export const useIssueListActions = () => {
  const wr = useWorkspaceRouter();
  const hideIssueMutation = useHideIssueMutation();
  const favoriteIssueMutation = useFavoriteIssueMutation();

  const hideIssue = useCallback(
    async (name: string, hidden: boolean) => {
      try {
        await hideIssueMutation.mutateAsync({ name, hidden });
        toast(`Issue ${hidden ? 'hidden' : 'unhidden'}`, 'success');
      } catch (error) {
        console.error(error);
        toast('Failed to hide issue', 'fail');
      }
    },
    [hideIssueMutation],
  );

  const favoriteIssue = useCallback(
    async (name: string, favorite: boolean) => {
      try {
        await favoriteIssueMutation.mutateAsync({ name, favorite });
        if (favorite) {
          toast('Added to favorites', 'success');
        } else {
          toast('Removed from favorites', 'success');
        }
      } catch (error) {
        console.error(error);
        toast('Failed to favorite issue', 'fail');
      }
    },
    [favoriteIssueMutation],
  );

  const navigateToIssueDetailPage = useCallback(
    (
      name: string,
      dynamicFilterSet: StructuredFilter,
      signalDatePreset: SignalDateSelectionInput,
    ) => {
      const urlSearchParams = buildIssueDetailSearchParams({
        dynamicFilterSet,
        signalDatePreset,
      });
      void wr.push(
        `/signals/${encodeURIComponent(name)}?${urlSearchParams.toString()}`,
      );
    },
    [wr],
  );

  return { hideIssue, favoriteIssue, navigateToIssueDetailPage };
};
